import React from 'react';
import MenuPancake from './MenuPancake';
import axios from 'axios';
import { serverDomain } from '../pages/config';
import ProfileIcon from './ProfileIcon';
import ProfileEditorDialog from './ProfileEditorDialog';

export default function AdminBar({ bgColor, textColor, onCreateNewApp, appName, appIconData = '', appIconStyle = '', chatPaneWidth, isMobile, isFullscreen, fixed = true, centerAppName = false, mobileRightAlign = false, showSearch = false, onSearch, onSearchClear, searchPlaceholder = 'Search apps...', showProfileIcon = false }) {
  const [autoMobile, setAutoMobile] = React.useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));
  const mobile = typeof isMobile === 'boolean' ? isMobile : autoMobile;
  const barHeight = mobile ? 56 : 60;
  const [createHover, setCreateHover] = React.useState(false);
  const [createActive, setCreateActive] = React.useState(false);
  const [displayName, setDisplayName] = React.useState(appName || '');
  const [isEditingName, setIsEditingName] = React.useState(false);
  const [nameValue, setNameValue] = React.useState(appName || '');
  const nameInputRef = React.useRef(null);
  const searchInputRef = React.useRef(null);
  const [searchValue, setSearchValue] = React.useState('');
  const [mobileSearchActive, setMobileSearchActive] = React.useState(false);
  const [toast, setToast] = React.useState({ show: false, message: '' });
  const [profileEditorOpen, setProfileEditorOpen] = React.useState(false);
  const previousNameRef = React.useRef(appName || '');
  const isEditingNameRef = React.useRef(false);

  React.useEffect(() => {
    if (typeof isMobile === 'boolean') return;
    const updateMobile = () => setAutoMobile(window.innerWidth < 768);
    updateMobile();
    window.addEventListener('resize', updateMobile);
    window.addEventListener('orientationchange', updateMobile);
    return () => {
      window.removeEventListener('resize', updateMobile);
      window.removeEventListener('orientationchange', updateMobile);
    };
  }, [isMobile]);

  React.useEffect(() => {
    if (!mobile && mobileSearchActive) setMobileSearchActive(false);
  }, [mobile, mobileSearchActive]);

  React.useEffect(() => {
    isEditingNameRef.current = isEditingName;
  }, [isEditingName]);

  React.useEffect(() => {
    if (!isEditingNameRef.current) {
      setDisplayName(appName || '');
      setNameValue(appName || '');
    }
  }, [appName]);

  const showToast = (message) => {
    setToast({ show: true, message });
    setTimeout(() => setToast({ show: false, message: '' }), 3000);
  };

  const handleSearchSubmit = () => {
    const query = (searchValue || '').trim();
    if (!query) {
      if (onSearchClear) onSearchClear();
      return;
    }
    if (onSearch) onSearch(query);
  };

  const handleSearchBlur = () => {
    if (mobile) setMobileSearchActive(false);
  };

  const handleSearchClear = () => {
    setSearchValue('');
    if (onSearchClear) onSearchClear();
    if (mobile) {
      setTimeout(() => setMobileSearchActive(false), 0);
      return;
    }
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  const activateMobileSearch = () => {
    if (!mobile) return;
    setMobileSearchActive(true);
    setTimeout(() => {
      if (searchInputRef.current) searchInputRef.current.focus();
    }, 30);
  };

  const hideForMobileSearch = mobile && mobileSearchActive;
  const showSearchInput = showSearch && (!mobile || mobileSearchActive);
  const showSearchIcon = showSearch && mobile && !mobileSearchActive;
  const resolvedAppName = displayName || appName || '';
  const resolvedAppInitial = (resolvedAppName || 'A').charAt(0).toUpperCase();

  let resolvedIconStyle = { background: '#ccc', textColor: '#000' };
  try {
    if (appIconStyle && typeof appIconStyle === 'string') {
      resolvedIconStyle = { ...resolvedIconStyle, ...JSON.parse(appIconStyle) };
    } else if (appIconStyle && typeof appIconStyle === 'object') {
      resolvedIconStyle = { ...resolvedIconStyle, ...appIconStyle };
    }
  } catch (err) {
    console.error('Error parsing app icon style:', err);
  }

  const renderAppIcon = (size, borderRadius) => (
    <div
      style={{
        width: size,
        height: size,
        borderRadius,
        background: appIconData ? 'transparent' : (resolvedIconStyle.background || '#ccc'),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: Math.round(size * 0.5),
        fontWeight: 'bold',
        color: resolvedIconStyle.textColor || '#000',
        overflow: 'hidden',
        flexShrink: 0
      }}
    >
      {appIconData ? (
        <img
          src={appIconData}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius, display: 'block' }}
        />
      ) : (
        resolvedAppInitial
      )}
    </div>
  );

  const renderDesktopIdentityContent = (fontSize) => (
    <>
      {renderAppIcon(32, 8)}
      {isEditingName ? (
        <input
          ref={nameInputRef}
          value={nameValue}
          onChange={(e) => setNameValue(e.target.value)}
          onBlur={commitRename}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitRename(); } else if (e.key === 'Escape') { e.preventDefault(); setIsEditingName(false); setNameValue(displayName); } }}
          spellCheck={false}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            minWidth: 0,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: textColor,
            fontSize,
            fontWeight: 400,
          }}
        />
      ) : (
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{resolvedAppName}</span>
      )}
    </>
  );

  const startEditName = () => {
    if (isEditingName) return; // prevent re-entering edit while already editing
    previousNameRef.current = displayName || '';
    setIsEditingName(true);
    setNameValue(displayName || '');
    setTimeout(() => { try { if (nameInputRef.current) { nameInputRef.current.focus(); nameInputRef.current.setSelectionRange(0, nameInputRef.current.value.length); } } catch (_) {} }, 30);
  };

  const commitRename = async () => {
    const newName = (nameValue || '').trim();
    const previous = previousNameRef.current;
    setIsEditingName(false);
    if (!newName) return;
    if (newName === previous) return;
    // Optimistic update: set immediately so UI reflects the change
    setDisplayName(newName);
    try { localStorage.setItem('appstar_app_name', newName); } catch (_) {}
    try {
      const token = localStorage.getItem('appstar_jwt');
      const app_id = localStorage.getItem('appstar_app_id');
      if (!token || !app_id) { showToast('Missing auth. Name not changed.'); throw new Error('missing-auth'); }
      await axios.patch(
        serverDomain + 'rename-app.php',
        null,
        { params: { token, app_id, new_name: newName } }
      );
      console.log('Rename success');
      // success: nothing else needed (already updated optimistically)
    } catch (err) {
      console.error('Rename error:', err);
      showToast('Could not rename app.');
      // revert visible name
      setDisplayName(previous);
      setNameValue(previous);
      try { localStorage.setItem('appstar_app_name', previous); } catch (_) {}
    }
  };

  const createButton = onCreateNewApp ? (
    <button
      title="Create a new app"
      onClick={onCreateNewApp}
      onMouseEnter={() => setCreateHover(true)}
      onMouseLeave={() => { setCreateHover(false); setCreateActive(false); }}
      onMouseDown={() => setCreateActive(true)}
      onMouseUp={() => setCreateActive(false)}
      style={{
        background: createActive ? (mobile ? '#0d0d0d22' : '#ffffff22') : (createHover ? (mobile ? '#0d0d0d11' : '#ffffff11') : 'transparent'),
        border: `1px solid ${createHover ? '#ffffff66' : '#ffffff33'}`,
        color: textColor,
        fontSize: '1rem',
        cursor: 'pointer',
        fontWeight: 'bold',
        padding: '0.25rem 0.65rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        borderRadius: 6,
        boxShadow: createActive ? 'inset 0 2px 4px rgba(0,0,0,0.25)' : (createHover ? '0 1px 4px rgba(0,0,0,0.15)' : 'none'),
        transition: 'background 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
        flexShrink: 0
      }}
    >
      <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>+</span> New app
    </button>
  ) : null;

  return (
    <>
    <div className="admin-bar" style={{ background: bgColor, color: textColor, justifyContent: 'space-between', position: fixed ? 'fixed' : 'relative', top: fixed ? 0 : undefined, left: fixed ? 0 : undefined, right: fixed ? 0 : undefined, width: '100%', zIndex: 8000, minHeight: barHeight, display: 'flex', alignItems: 'center', padding: '0 8px' }}>
      <MenuPancake bgColor={bgColor} textColor={textColor} isMobile={mobile} showBrand={!hideForMobileSearch} />
      {showSearchIcon && (
        <button
          onClick={activateMobileSearch}
          aria-label="Search apps"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.35)',
            color: textColor,
            width: 34,
            height: 34,
            borderRadius: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>
      )}
      {showSearchInput && (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: mobile ? '70%' : '40%',
            minWidth: mobile ? 0 : 220,
            maxWidth: mobile ? '90%' : 520,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <input
            ref={searchInputRef}
            type="text"
            value={searchValue}
            onChange={(e) => {
              const nextValue = e.target.value;
              setSearchValue(nextValue);
              const query = nextValue.trim();
              if (!query) {
                if (onSearchClear) onSearchClear();
                return;
              }
              if (onSearch) onSearch(query);
            }}
            onBlur={handleSearchBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearchSubmit();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                e.currentTarget.blur();
              }
            }}
            placeholder={searchPlaceholder}
            spellCheck={false}
            aria-label="Search apps"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '8px 34px 8px 12px',
              borderRadius: 18,
              border: '1px solid rgba(255,255,255,0.35)',
              background: 'rgba(255,255,255,0.12)',
              color: textColor,
              outline: 'none',
              fontSize: mobile ? '0.95rem' : '1rem'
            }}
          />
          {searchValue && (
            <button
              type="button"
              aria-label="Clear search"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSearchClear();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              style={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 20,
                height: 20,
                borderRadius: 10,
                border: 'none',
                background: 'rgba(255,255,255,0.25)',
                color: textColor,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0
              }}
            >
              &times;
            </button>
          )}
        </div>
      )}
      {!centerAppName && resolvedAppName && mobile && !hideForMobileSearch && (
        <div
          style={mobileRightAlign ? {
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 0,
            display: 'flex',
            alignItems: 'center'
          } : {
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 0,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {renderAppIcon(34, 8)}
        </div>
      )}
      {!centerAppName && resolvedAppName && !mobile && !isFullscreen && !hideForMobileSearch && (
        <div
          style={{
            position: 'absolute',
            left: `calc(${chatPaneWidth}% + 6.8rem)`,
            top: '50%',
            transform: 'translateY(-50%)',
            color: textColor,
            fontSize: '1rem',
            fontWeight: 400,
            pointerEvents: 'auto',
            zIndex: 0,
            maxWidth: 'calc(60vw - 33.33% - 2rem)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
          onClick={startEditName}
          onTouchEnd={startEditName}
        >
          {renderDesktopIdentityContent('1rem')}
        </div>
      )}
      {!centerAppName && resolvedAppName && !mobile && isFullscreen && !hideForMobileSearch && (
        <div
          style={{
            position: 'absolute',
            left: `calc(${chatPaneWidth}% + 8rem)`,
            top: '50%',
            transform: 'translateY(-50%)',
            color: textColor,
            fontSize: '1rem',
            fontWeight: 400,
            pointerEvents: 'auto',
            zIndex: 0,
            maxWidth: `calc(100vw - ${chatPaneWidth}% - 8.5rem)`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
          onClick={startEditName}
          onTouchEnd={startEditName}
        >
          {renderDesktopIdentityContent('1rem')}
        </div>
      )}
      {centerAppName && appName && (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            color: textColor,
            fontSize: '1rem',
            fontWeight: 400,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 0,
            maxWidth: '60%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'center'
          }}
        >
          {appName}
        </div>
      )}
      {(createButton || showProfileIcon) && !hideForMobileSearch && (
        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            paddingRight: mobile ? 4 : 8,
            zIndex: 1
          }}
        >
          {createButton}
          {showProfileIcon && (
            <button
              type="button"
              aria-label="Open profile editor"
              onClick={() => setProfileEditorOpen(true)}
              style={{
                border: 'none',
                background: 'transparent',
                padding: 0,
                margin: 0,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <ProfileIcon size={mobile ? 34 : 38} />
            </button>
          )}
        </div>
      )}
    </div>
    <ProfileEditorDialog open={profileEditorOpen} onClose={() => setProfileEditorOpen(false)} />
    {toast.show && (
      <div style={{ position: fixed ? 'fixed' : 'absolute', top: fixed ? (mobile ? barHeight + 6 : barHeight + 8) : 6, left: '50%', transform: 'translateX(-50%)', background: '#ff4d4f', color: '#fff', padding: '6px 10px', borderRadius: 6, fontSize: '0.85rem', zIndex: 9000, boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}>
        {toast.message}
      </div>
    )}
    {/* Spacer to push page content below fixed admin bar */}
    {fixed && <div style={{ height: barHeight }} />}
    </>
  );
}
